import React, { useContext, SyntheticEvent, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

//MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';

//Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogHeader from '@/ui/Dialog/DialogHeader';
import DialogFooter from '@/ui/Dialog/DialogFooter';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogBody from '@/ui/Dialog/DialogBody';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItem from '@/ui/Dialog/DialogToolbarMenuItem';
import DialogContext from '@/ui/DialogContext/DialogContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import { GridContainer, GridItem } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import { Permissions } from '@/lib/permissions';
import FormAvatar from '@/ui/Form/FormAvatar';
import FormHeading from '@/ui/Form/FormHeading';

//Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { IDialogProps } from '@/@types/ui/DialogContext';
import { Delete } from '@mui/icons-material';
import LabelIcon from '@mui/icons-material/Label';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';

const IzletiOpisDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { mode, onClose, dc } = props;

  const { record, validation, dataChanged, fields, subModels, onFieldChange, doClose, doDelete, doInsert, doUpdate, doClear } =
    useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }

    // Autofill poglavlja_id which have ui_default_text defined in its picker records
    // TODO: think about if we dont want to overwrite sadrzaj if user has already written something
    if (source === "poglavlje_id" && value.record && value.record.ui_default_text) {
      onFieldChange(value.record.ui_default_text, "sadrzaj")
    }
  };

  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            snackbarContext.showNotification(t('notifications.insert'), 'success');
            // dont close dialog but clear form 
            doClear(true);
            close({ dataChanged: true, action: 'insert' }, true);
          } else if (result.error) {
            const msg = t('error.addRecord') + result.error;
            snackbarContext.showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            //do nothing
          }
        })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = (evt: SyntheticEvent) => {
    doUpdate().then((result) => {
      if (result.success) {
        snackbarContext.showNotification(t('notifications.update'), 'success');
        close({ dataChanged: true, action: 'update' });
      } else if (result.error) {
        const msg = t('error.editRecord') + result.error;
        snackbarContext.showNotification(msg, 'error');
      } else if (result.validationPass === false) {
        //do nothing
      }
    });
  };

  const handleClose = (evt: SyntheticEvent) => {
    doClose()
      .then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: 'cancel' });
        } else {
          if (result.shouldSave) {
            handleUpdate(evt);
          } else if (result.canceled) {
            //do nothing
          }
        }
      })
      .catch((result) => {
        console.log(result);
      });
  };

  const handleDelete = () => {
    doDelete().then((result) => {
      if (result.success) {
        snackbarContext.showNotification(t('notifications.delete'), 'success');
        close({ dataChanged: true, action: 'delete' });
      } else if (result.error) {
        const msg = t('error.delete') + result.error;
        snackbarContext.showNotification(msg, 'error');
      }
    });
  };

  const close = (result: ClosingDetails, dontHide: boolean = false) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
    }
    if (!dontHide) {
      dialogContext.hideDialog(2);
    }
   
  };

  const title = t('titles.izlet_opis');
  const disableSave = dataChanged ? false : true;

  const allowDelete = mode === 'update'; // TODO: how to fix, maybe pass prop to check?

  const hasToolbarActions = allowDelete;

  const icon = <LabelIcon />;

  return (
    <DynamicDialog open={true} maxWidth={'sm'} fixedHeight={true} onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters={true}>
          <FormAvatar icon={icon} ariaLabel="opis" />
          <FormHeading header={title} />
          <ToolbarFillContent />
          {hasToolbarActions ? (
            <DialogToolbarMenuButton>
              {mode === 'update' ? <DialogToolbarMenuItem onClick={handleDelete} label={t('buttons.delete')} icon={<Delete />} /> : null}
            </DialogToolbarMenuButton>
          ) : null}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            <FormContent
              fieldNames={dc.getFormFieldsNames('default')}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={handleFieldChange}
              mode={mode}
              columns={1}
              subModels={subModels}
            />
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogFooter>
        {mode === 'view' ? (
          <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        ) : (
          <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        )}
        {mode === 'insert' ? <DialogActionButtonAdd continueAdd variant="contained" onClick={handleInsert} /> : null}
        {mode === 'update' ? <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={disableSave} /> : null}
      </DialogFooter>
    </DynamicDialog>
  );
};

export default IzletiOpisDialog;
